import React, {
  createContext,
  ReactNode,
  FC,
  useState,
  useEffect,
  useContext,
  useRef,
} from "react";
import { View } from "react-native";
import { useNavigation, StackActions } from "@react-navigation/native";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { ShowLoader } from "../common/ShowLoader";
import { AppScreens } from "../screens/Utility/AppScreens";
import { checkAndHandleTokenExpiration } from "../utility/auth/authTokenHandler";
import { getActiveSubscription } from "../services/profileAPI";
import { handleApiError } from "../services/errorHandler";
import { clearAllCaches } from "../utility/cacheManager";

interface AuthContextProps {
  isLoggedIn: boolean;
  setIsLoggedIn: (loggedIn: boolean) => void;
  loading: boolean;
  showModal: boolean;
  setShowModal: (show: boolean) => void;
  modalType: string;
  setModalType: (type: string) => void;
  isActiveSubscriptionFound: boolean;
  refreshLoginState: () => Promise<void>;
  hasJustLoggedIn: boolean;
  setHasJustLoggedIn: (value: boolean) => void;
}

export const AuthContext = createContext<AuthContextProps>({
  isLoggedIn: false,
  setIsLoggedIn: () => { },
  loading: true,
  showModal: false,
  setShowModal: () => { },
  modalType: "signup",
  setModalType: () => { },
  isActiveSubscriptionFound: false,
  refreshLoginState: async () => { },
  hasJustLoggedIn: false,
  setHasJustLoggedIn: () => { },
});

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: FC<AuthProviderProps> = ({ children }) => {
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [modalType, setModalType] = useState<string>("signup");
  const [isActiveSubscriptionFound, setIsActiveSubscriptionFound] =
    useState<boolean>(false);
  const [subscriptionFetched, setSubscriptionFetched] =
    useState<boolean>(false);
  const [hasJustLoggedIn, setHasJustLoggedIn] = useState<boolean>(false);

  const navigation = useNavigation();
  const navigationRef = useRef(navigation);

  useEffect(() => {
    navigationRef.current = navigation;
  }, [navigation]);

  // Track if this is the initial mount to prevent false "just logged in" detection
  const isInitialMount = useRef(true);

  const refreshLoginState = async () => {
    try {
      const jsonValue = await AsyncStorage.getItem("@loginData");
      let data = null;

      if (jsonValue) {
        try {
          data = JSON.parse(jsonValue);
        } catch {
          await AsyncStorage.removeItem("@loginData");
        }
      }

      const wasLoggedOut = !isLoggedIn;
      const willBeLoggedIn = !!data;

      setIsLoggedIn(willBeLoggedIn);
      setShowModal(!data);
      setLoading(false);

      // Set flag when user just logged in (but not on initial page load)
      if (wasLoggedOut && willBeLoggedIn && !isInitialMount.current) {
        console.log("🎉 First-time login detected in AuthContext");
        setHasJustLoggedIn(true);
        // Reset the flag after a short delay
        setTimeout(() => setHasJustLoggedIn(false), 2000);
      }

      // Mark that initial mount is complete
      if (isInitialMount.current) {
        isInitialMount.current = false;
        console.log(
          "✅ AuthContext initial mount complete. isLoggedIn:",
          willBeLoggedIn
        );
      }

      if (!data) {
        // Clear all caches when no login data is found
        await clearAllCaches();
      }

      if (data?.username && !subscriptionFetched) {
        try {
          const response = await getActiveSubscription(data.username);
          if (response.statusCode === 200) {
            setIsActiveSubscriptionFound(
              response.body.isActiveSubscriptionFound
            );
          } else {
            handleApiError(response.statusCode, response.error);
          }
        } catch (error) {
          console.error("Subscription fetch error:", error);
        }
        setSubscriptionFetched(true);
      }
    } catch (error) {
      console.error("Auth state error:", error);
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshLoginState();
    checkAndHandleTokenExpiration(navigationRef.current);
  }, []);

  // Track if we've already performed initial navigation to prevent loops
  const hasPerformedInitialNav = useRef(false);

  useEffect(() => {
    if (isLoggedIn && navigationRef.current) {
      try {
        const params = new URLSearchParams(window.location.search);
        const eventID = params.get("eventID");
        const session = params.get("session");
        const invite = params.get("invite");
        const redirectPath = params.get("redirect");

        // Check current path from window location to avoid unnecessary navigation
        const currentPath = window.location.pathname;
        console.log(
          "🔍 AuthContext navigation check - Current path:",
          currentPath,
          "hasPerformedInitialNav:",
          hasPerformedInitialNav.current
        );

        const isOnLoginScreen =
          currentPath.includes("login") ||
          currentPath.includes("signup") ||
          currentPath.includes("setup");
        const isOnAuthenticatedScreen = !isOnLoginScreen && currentPath !== "/";

        // Only navigate if we're on login/signup/setup screens OR have special params that require navigation
        // This prevents unnecessary navigation when user is already on an authenticated screen
        const hasSpecialParams = session || invite || eventID || redirectPath;
        const shouldNavigate = isOnLoginScreen || hasSpecialParams;

        console.log("🔍 Navigation decision:", {
          isOnLoginScreen,
          isOnAuthenticatedScreen,
          hasSpecialParams,
          shouldNavigate,
          hasPerformedInitialNav: hasPerformedInitialNav.current,
        });

        // Skip if we've already navigated once and user is on authenticated screen
        if (hasPerformedInitialNav.current && !hasSpecialParams) {
          console.log("⏭️ Skipping - already performed initial navigation");
          return;
        }

        // Check for pending challenge accept FIRST (returning from Stripe payment)
        // This must happen before the "already on authenticated screen" check
        const pendingChallengeStr = sessionStorage.getItem('pendingChallengeAccept');
        if (pendingChallengeStr) {
          try {
            const pendingChallenge = JSON.parse(pendingChallengeStr);
            // Check not expired (30 minutes)
            if (Date.now() - pendingChallenge.timestamp < 30 * 60 * 1000) {
              console.log("📍 Found pending challenge in AuthContext, navigating to Notifications");
              // Clear the pending challenge to prevent redirect loop
              sessionStorage.removeItem('pendingChallengeAccept');
              // Use window.location.href for proper URL-based navigation (works with middleware)
              if (typeof window !== 'undefined') {
                window.location.href = '/notifications';
              } else {
                navigationRef.current.dispatch(
                  StackActions.replace(AppScreens.NotificationScreen)
                );
              }
              hasPerformedInitialNav.current = true;
              return;
            } else {
              // Clear expired pending challenge
              console.log("📍 Pending challenge expired, clearing");
              sessionStorage.removeItem('pendingChallengeAccept');
            }
          } catch (e) {
            console.error("Error parsing pending challenge:", e);
            sessionStorage.removeItem('pendingChallengeAccept');
          }
        }

        if (!shouldNavigate && isOnAuthenticatedScreen) {
          console.log(
            "⏭️ Skipping navigation - user already on authenticated screen:",
            currentPath
          );
          hasPerformedInitialNav.current = true;
          return;
        }

        // Handle session and invite parameters - route to Notifications
        if (session || invite) {
          // If we are already on the notification screen, do NOT redirect again
          if (currentPath.includes("notifications")) {
            console.log(
              "⏭️ Skipping navigation - user already on notifications screen with params"
            );
            hasPerformedInitialNav.current = true;
            return;
          }

          const notificationParams: any = {};
          if (session) notificationParams.session = session;
          if (invite) notificationParams.invite = invite;

          console.log(
            "📍 Navigating to Notifications with params:",
            notificationParams
          );
          navigationRef.current.dispatch(
            StackActions.replace(
              AppScreens.NotificationScreen,
              notificationParams
            )
          );
          hasPerformedInitialNav.current = true;
        } else if (eventID) {
          console.log(
            "📍 Navigating with eventID to:",
            redirectPath || AppScreens.HomeScreen
          );
          navigationRef.current.dispatch(
            StackActions.replace(redirectPath || AppScreens.HomeScreen, {
              eventID,
            })
          );
          hasPerformedInitialNav.current = true;
        } else if (redirectPath) {
          console.log("📍 Navigating to redirectPath:", redirectPath);
          navigationRef.current.dispatch(StackActions.replace(redirectPath));
          hasPerformedInitialNav.current = true;
        } else if (isOnLoginScreen) {
          // Only navigate to HomeScreen if coming from login/signup screens
          // (Pending challenge check is now handled earlier in the flow)
          console.log("📍 Navigating from login screen to HomeScreen");
          navigationRef.current.dispatch(
            StackActions.replace(AppScreens.HomeScreen)
          );
          hasPerformedInitialNav.current = true;
        }
      } catch (error) {
        console.error("Navigation error:", error);
      }
    }
  }, [isLoggedIn]);

  return (
    <AuthContext.Provider
      value={{
        isLoggedIn,
        setIsLoggedIn,
        loading,
        showModal,
        setShowModal,
        modalType,
        setModalType,
        isActiveSubscriptionFound,
        refreshLoginState,
        hasJustLoggedIn,
        setHasJustLoggedIn,
      }}
    >
      <View style={{ flex: 1 }}>
        {loading ? <ShowLoader ActivityIndicator={loading} /> : children}
      </View>
    </AuthContext.Provider>
  );
};

export const useAuthProvider = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuthProvider must be used within an AuthProvider");
  }
  return context;
};
